/**
 * Helper functions for building class names for React components
 */

const createClassNameStringFromArray = (classArray) => classArray.join(' ');

/**
 * Creates classes using the `baseClassName--variant` style
 * @param  {string} baseClassName
 * @param  {string} variations
 * @return {string}
 */
export function buildClassNamesFromVariations(baseClassName, variations = '') {
    const builtClassNames = [];
    variations &&
        variations
            .split(' ')
            .filter((variation) => variation)
            .forEach((variation) => {
                builtClassNames.push(`${baseClassName}--${variation}`);
            });
    return createClassNameStringFromArray(builtClassNames);
}

export function buildClassNamesFromComponent(component, baseClassName) {
    const { props } = component;
    const builtClassNames = [baseClassName];
    let componentCustomClassNames;

    // If component has the custom class name building function, propClassNames
    if (component.propClassNames) {
        componentCustomClassNames = component.propClassNames(props, baseClassName);

        builtClassNames.push(
            Array.isArray(componentCustomClassNames)
                ? createClassNameStringFromArray(componentCustomClassNames)
                : componentCustomClassNames,
        );
    }

    // Custom class names from props
    props.className && builtClassNames.push(props.className);

    if (props.variation) {
        builtClassNames.push(buildClassNamesFromVariations(baseClassName, props.variation));
    }

    return createClassNameStringFromArray(builtClassNames);
}

/**
 * Creates a class name string from the constructors name and a passed in className prop
 * @param  {string} baseClassName
 * @param  {string} classNameProp
 * @return {string}
 */
export function buildClassNamesWithCustomClassNameProp(baseClassName, classNameProp) {
    if (typeof classNameProp !== 'string') {
        return baseClassName;
    }

    return createClassNameStringFromArray([baseClassName, classNameProp]);
}

/**
 * Build a className from an object. Inspired by JedWatson/classnames
 *
 * @param  {object} sourceObject Keys become classes. Truthiness of values determine if the class
 *                               is applied
 * @return {string}              String of HTML appropriate class names
 */
export function classNames(sourceObject) {
    return Object.keys(sourceObject)
        .filter((key) => sourceObject[key])
        .reduce((classString, key) => `${classString.length ? `${classString} ` : ''}${key}`, '');
}
